import React, { useEffect, useState, useRef } from 'react';
import { onObserverMessageRecived } from '../../../Socket/socket';
import './observer.css';
import styled from 'styled-components';
import DOMPurify from 'dompurify';

const ConversationObserver = ({ observerId }) => {
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [feedbacks, setFeedbacks] = useState({});
  const [currentEmotion, setCurrentEmotion] = useState('neutral');
  const instanceId = useRef(`observer-${Math.random().toString(36).substr(2, 9)}`);

  // Observer message handling
  useEffect(() => {
    const handleObserverMessage = (data) => {
      console.log('Observer message received:', data);
      console.log('Raw data content:', data.data);
      
      // Check if data exists and has the expected structure
      if (!data || typeof data !== 'object') {
        console.error('Invalid data received in observer:', data);
        return;
      }
      
      // Safely process the data
      let processedMessage;
      if (typeof data.data === 'string') {
        // If data.data is a string, process it as before
        processedMessage = data.data.replace(/^```html\n/, '').replace(/\n```$/, '');
      } else {
        // If data.data is not a string or is undefined, handle accordingly
        console.warn('Received observer data without expected string format:', data);
        // Use a fallback format that works with your rendering logic
        processedMessage = `
          <div>
            [Priority: Low]
            [Type: System Message]
            Context: Received data in unexpected format.
            Suggestion: Check the console for the raw data.
            Emotion[neutral]
          </div>
        `;
      }
      
      // Check if message only contains "[Got the info]" and skip if it does
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = processedMessage;
      const textContent = tempDiv.textContent.trim();
      
      if (textContent === "[Got the info]") {
        console.log('Skipping "[Got the info]" message in observer');
        return; // Skip adding this message to the state
      }
      
      // Extract emotion from the message before adding to state
      const emotionMatch = processedMessage.match(/Emotion\[(.*?)\]/i);
      let detectedEmotion = 'neutral';
      
      console.log('Emotion match result:', emotionMatch);
      
      if (emotionMatch) {
        detectedEmotion = emotionMatch[1].toLowerCase().trim();
        console.log('Found emotion tag:', detectedEmotion);
      } else {
        // If no emotion tag, try to infer from context
        detectedEmotion = inferEmotionFromContext(processedMessage);
        console.log('Inferred emotion from context:', detectedEmotion);
      }
      
      console.log('Processed message:', processedMessage);
      console.log('Final detected emotion:', detectedEmotion);
      setCurrentEmotion(detectedEmotion);

      // Add to messages state (FIFO: new messages at top, max 5 messages)
      setMessages((prevMessages) => {
        // Add new message at the beginning (top)
        const newMessages = [processedMessage, ...prevMessages];
        
        // Keep only the latest 5 messages
        const limitedMessages = newMessages.slice(0, 5);
        
        // Update feedbacks for the new message structure
        setFeedbacks(prev => {
          const newFeedbacks = {};
          limitedMessages.forEach((_, index) => {
            newFeedbacks[index] = prev[index] || { helpful: null, rating: 7, comment: '' };
          });
          return newFeedbacks;
        });
        
        return limitedMessages;
      });

      setTimeout(() => {
        if (messagesEndRef.current) {
          // Scroll to top since new messages appear at the top
          messagesEndRef.current.scrollTop = 0;
        }
      }, 1)
    };

    const unsubscribe = onObserverMessageRecived(handleObserverMessage);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Add event listener for insert response buttons
  useEffect(() => {
    // Store a reference to the observer panel
    const observerPanel = document.querySelector('.observer-panel');
    
    // Define the event handler
    const handleInsertResponse = (e) => {
      const button = e.target.closest('.insert-response-btn');
      const clickableText = e.target.closest('.auto-response-content');
      
      // Check if either button or clickable text was clicked
      if (!button && !clickableText) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      // Get response text from either button or clickable text
      const responseText = button 
        ? decodeURIComponent(button.getAttribute('data-response'))
        : decodeURIComponent(clickableText.getAttribute('data-response'));
        
      console.log('Inserting response text:', responseText);
      
      // Target the specific input element with class "sc-kXaPYj kfmGvK"
      const conversationInput = 
        document.querySelector('.conversation-input-ref')
      
      // If we found the input element
      if (conversationInput) {
        console.log('Found conversation input:', conversationInput);
        
        // Set the value directly
        conversationInput.value = responseText;
        
        // Focus the input
        conversationInput.focus();
        
        // Create and dispatch input event to trigger React state update
        const inputEvent = new Event('input', { bubbles: true });
        conversationInput.dispatchEvent(inputEvent);
        
        // Also dispatch a change event
        const changeEvent = new Event('change', { bubbles: true });
        conversationInput.dispatchEvent(changeEvent);
        
        // Try to access React's onChange handler directly
        try {
          // Find the React props key
          const propsKey = Object.keys(conversationInput).find(key => 
            key.startsWith('__reactProps$') || key.startsWith('__reactEventHandlers$')
          );
          
          if (propsKey && conversationInput[propsKey].onChange) {
            // Create a synthetic event
            const syntheticEvent = {
              target: conversationInput,
              currentTarget: conversationInput,
              preventDefault: () => {},
              stopPropagation: () => {}
            };
            
            // Call the onChange handler
            conversationInput[propsKey].onChange(syntheticEvent);
            console.log('Successfully triggered React onChange handler');
          }
        } catch (err) {
          console.error('Error accessing React props:', err);
        }
      } else {
        console.error('Could not find conversation input element');
        
        // Fallback: Copy to clipboard with notification
        navigator.clipboard.writeText(responseText)
          .then(() => {
            // Create a temporary notification element
            const notification = document.createElement('div');
            notification.textContent = 'Response copied to clipboard!';
            notification.style.position = 'fixed';
            notification.style.bottom = '20px';
            notification.style.left = '50%';
            notification.style.transform = 'translateX(-50%)';
            notification.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
            notification.style.color = 'white';
            notification.style.padding = '10px 20px';
            notification.style.borderRadius = '5px';
            notification.style.zIndex = '9999';
            
            document.body.appendChild(notification);
            
            // Remove the notification after 3 seconds
            setTimeout(() => {
              if (document.body.contains(notification)) {
                document.body.removeChild(notification);
              }
            }, 3000);
          })
          .catch(err => {
            console.error('Failed to copy text: ', err);
          });
      }
    };

    // Add event listener only if the panel exists
    if (observerPanel) {
      observerPanel.addEventListener('click', handleInsertResponse);
      
      // Return cleanup function
      return () => {
        // Check if the element still exists before removing the listener
        if (observerPanel && document.body.contains(observerPanel)) {
          observerPanel.removeEventListener('click', handleInsertResponse);
        }
      };
    }
    
    // Return empty cleanup function if panel doesn't exist
    return () => {};
  }, []);

  const formatMessage = (htmlString) => {
    const div = document.createElement('div');
    div.innerHTML = htmlString;
    const text = div.textContent;
    
    // Handle Priority
    const priorityMatch = text.match(/\[Priority:\s*(High|Medium|Low)\]/i);
    const priority = priorityMatch ? priorityMatch[1] : 'Low';
    let modifiedHtml = htmlString;
    
    // Hide the priority and type tags
    modifiedHtml = modifiedHtml.replace(/\[Priority:\s*(High|Medium|Low)\]/i, '');
    modifiedHtml = modifiedHtml.replace(/\[Type:\s*(.*?)\]/i, '');
    
    // Replace Emotion tags
    modifiedHtml = modifiedHtml.replace(/Emotion\[(.*?)\]/i, '');
    
    // Create a table layout for the entire content to prevent shifting
    let processedHtml = '<table style="width:100%; border-spacing:0; border-collapse:collapse; font-size:12px;">';
    
    // Check if we have Context section
    const contextMatch = modifiedHtml.match(/(Context:)\s*(.*?)(?=\s*Suggestion:|$)/s);
    if (contextMatch) {
      processedHtml += `
        <tr>
          <td style="color:rgba(255,255,255,0.6); padding-bottom:6px; vertical-align:top; white-space:nowrap;">Context:</td>
          <td style="color:rgba(255,255,255,0.9); padding-bottom:6px; padding-left:5px; vertical-align:top;">${contextMatch[2]}</td>
        </tr>
      `;
    }
    
    // Check if we have Suggestion section
    const suggestionMatch = modifiedHtml.match(/(Suggestion:)\s*(.*?)(?=\s*Emotion|AutoResponse|$)/s);
    if (suggestionMatch) {
      processedHtml += `
        <tr>
          <td style="color:rgba(255,255,255,0.6); padding-bottom:6px; vertical-align:top; white-space:nowrap;">Suggestion:</td>
          <td style="color:rgba(255,255,255,0.9); padding-bottom:6px; padding-left:5px; vertical-align:top;">${suggestionMatch[2]}</td>
        </tr>
      `;
    }
    
    // Check if we have AutoResponse section
    const autoResponseMatch = modifiedHtml.match(/(AutoResponse:)\s*"(.*?)"/s);
    if (autoResponseMatch) {
      const content = autoResponseMatch[2];
      processedHtml += `
        <tr>
          <td style="color:rgba(255,255,255,0.6); vertical-align:top; white-space:nowrap;">AutoResponse:</td>
          <td style="padding-left:5px; vertical-align:top;">
            <a href="#" class="auto-response-content" data-response="${encodeURIComponent(content)}" style="color:rgb(82,180,252); text-decoration:underline;">${content}</a>
          </td>
        </tr>
      `;
    }
    
    processedHtml += '</table>';
    
    return {
      priority,
      modifiedHtml: processedHtml
    };
  };

  const handleFeedback = (index, isHelpful) => {
    setFeedbacks(prev => ({
      ...prev,
      [index]: { ...prev[index], helpful: isHelpful, rating: 7, comment: '' }
    }));
  };

  const handleRating = (index, rating) => {
    setFeedbacks(prev => ({
      ...prev,
      [index]: { ...prev[index], rating: rating }
    }));
  };

  const handleComment = (index, comment) => {
    setFeedbacks(prev => ({
      ...prev,
      [index]: { ...prev[index], comment: comment }
    }));
  };

  // Function to infer emotion from context content
  const inferEmotionFromContext = (messageContent) => {
    const text = messageContent.toLowerCase();
    
    // Keywords for different emotions (matching the allowed emotion list)
    const emotionKeywords = {
      // Urgency and stress
      urgency: ['urgent', 'urgency', 'immediate', 'asap', 'quickly', 'rush'],
      distress: ['distress', 'distressed', 'panic', 'crisis', 'emergency'],
      frustration: ['frustration', 'frustrated', 'annoyed', 'irritated', 'fed up'],
      overwhelmed: ['overwhelmed', 'too much', 'can\'t handle', 'stressed out'],
      impatient: ['impatient', 'waiting too long', 'taking forever', 'hurry up'],
      
      // Confusion and uncertainty
      confusion: ['confused', 'unclear', 'don\'t understand', 'puzzled', 'lost', 'bewildered'],
      hesitation: ['hesitant', 'unsure', 'maybe', 'not sure', 'uncertain about'],
      uncertain: ['uncertain', 'not sure', 'doubt', 'questioning', 'unclear'],
      skeptical: ['skeptical', 'doubt', 'suspicious', 'questionable', 'not convinced'],
      
      // Defensive and cooperative
      defensive: ['defensive', 'attacked', 'criticized', 'blame', 'fault'],
      cooperative: ['cooperative', 'helpful', 'work together', 'collaborate', 'assist'],
      engaged: ['engaged', 'interested', 'involved', 'participating', 'active'],
      
      // Positive emotions
      relieved: ['relieved', 'glad', 'thankful', 'phew', 'finally'],
      satisfied: ['satisfied', 'content', 'pleased', 'happy with'],
      hopeful: ['hopeful', 'optimistic', 'looking forward', 'positive'],
      enthusiastic: ['enthusiastic', 'excited', 'thrilled', 'eager', 'passionate'],
      motivated: ['motivated', 'determined', 'ready', 'inspired', 'driven'],
      grateful: ['grateful', 'thankful', 'appreciate', 'thanks', 'thank you'],
      confident: ['confident', 'sure', 'certain', 'positive', 'convinced'],
      optimistic: ['optimistic', 'positive', 'bright side', 'hopeful', 'good outcome'],
      
      // Negative emotions
      disappointed: ['disappointed', 'let down', 'expected more', 'unsatisfied'],
      worried: ['worried', 'concern', 'anxious', 'nervous', 'fear'],
      negative: ['negative', 'bad', 'poor', 'terrible', 'awful'],
      pessimistic: ['pessimistic', 'negative', 'worst case', 'doubt', 'won\'t work'],
      
      // Calm and cautious
      calm: ['calm', 'peaceful', 'relaxed', 'composed', 'patient'],
      cautious: ['cautious', 'careful', 'wary', 'hesitant', 'prudent'],
      
      // Core emotions
      positive: ['positive', 'good', 'great', 'excellent', 'wonderful', 'amazing'],
    };
    
    // Check for emotion keywords
    for (const [emotion, keywords] of Object.entries(emotionKeywords)) {
      for (const keyword of keywords) {
        if (text.includes(keyword)) {
          return emotion;
        }
      }
    }
    
    // Check for context patterns
    if (text.includes('repeating') || text.includes('repeated')) {
      return 'frustrated';
    }
    
    if (text.includes('wait') && (text.includes('long') || text.includes('too long'))) {
      return 'frustrated';
    }
    
    if (text.includes('repeatedly expressed frustration')) {
      return 'frustrated';
    }
    
    if (text.includes('expressing') && text.includes('frustration')) {
      return 'frustrated';
    }
    
    if (text.includes('immediately') || text.includes('urgent')) {
      return 'worried';
    }
    
    return 'neutral';
  };

  // Function to get emotion icon and color
  const getEmotionDisplay = (emotion) => {
    const emotionMap = {
      // Core emotions
      neutral: { icon: '😐', color: '#607D8B', label: 'Neutral' },
      positive: { icon: '😊', color: '#4CAF50', label: 'Positive' },
      negative: { icon: '😔', color: '#9E9E9E', label: 'Negative' },
      
      // Urgency and stress
      urgency: { icon: '⚡', color: '#FF5722', label: 'Urgent' },
      distress: { icon: '😰', color: '#D32F2F', label: 'Distressed' },
      frustration: { icon: '😤', color: '#E91E63', label: 'Frustrated' },
      overwhelmed: { icon: '😵', color: '#8E24AA', label: 'Overwhelmed' },
      impatient: { icon: '😑', color: '#FF7043', label: 'Impatient' },
      
      // Confusion and uncertainty
      confusion: { icon: '😕', color: '#9C27B0', label: 'Confused' },
      hesitation: { icon: '🤔', color: '#795548', label: 'Hesitant' },
      uncertain: { icon: '😟', color: '#6A4C93', label: 'Uncertain' },
      skeptical: { icon: '🤨', color: '#546E7A', label: 'Skeptical' },
      
      // Defensive and cooperative
      defensive: { icon: '😤', color: '#F44336', label: 'Defensive' },
      cooperative: { icon: '🤝', color: '#2196F3', label: 'Cooperative' },
      engaged: { icon: '😊', color: '#00BCD4', label: 'Engaged' },
      
      // Positive emotions
      relieved: { icon: '😌', color: '#4CAF50', label: 'Relieved' },
      satisfied: { icon: '😊', color: '#8BC34A', label: 'Satisfied' },
      hopeful: { icon: '🌟', color: '#FFC107', label: 'Hopeful' },
      enthusiastic: { icon: '🤩', color: '#FF9800', label: 'Enthusiastic' },
      motivated: { icon: '💪', color: '#FF5722', label: 'Motivated' },
      grateful: { icon: '🙏', color: '#4CAF50', label: 'Grateful' },
      confident: { icon: '😎', color: '#2196F3', label: 'Confident' },
      optimistic: { icon: '😄', color: '#CDDC39', label: 'Optimistic' },
      
      // Negative emotions
      disappointed: { icon: '😞', color: '#9E9E9E', label: 'Disappointed' },
      worried: { icon: '😟', color: '#FF5722', label: 'Worried' },
      
      // Calm and cautious
      calm: { icon: '😌', color: '#00BCD4', label: 'Calm' },
      cautious: { icon: '🤔', color: '#607D8B', label: 'Cautious' },
      pessimistic: { icon: '😒', color: '#757575', label: 'Pessimistic' },
      
      // Legacy emotions (for backward compatibility)
      happy: { icon: '😊', color: '#4CAF50', label: 'Happy' },
      sad: { icon: '😢', color: '#2196F3', label: 'Sad' },
      angry: { icon: '😠', color: '#F44336', label: 'Angry' },
      excited: { icon: '🤩', color: '#FF9800', label: 'Excited' },
      confused: { icon: '😕', color: '#9C27B0', label: 'Confused' },
      frustrated: { icon: '😤', color: '#E91E63', label: 'Frustrated' }, // Alternative form
      surprised: { icon: '😲', color: '#CDDC39', label: 'Surprised' },
      anxious: { icon: '😟', color: '#795548', label: 'Anxious' },
      content: { icon: '😊', color: '#8BC34A', label: 'Content' }
    };
    
    return emotionMap[emotion] || emotionMap.neutral;
  };

  return (
    <div className="observer-panel" style={{ position: 'relative' }}>
      {/* Emotion Display Area */}
      <div className="emotion-display-area" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 12px',
        marginBottom: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: `1px solid ${getEmotionDisplay(currentEmotion).color}40`,
        borderRadius: '8px',
        transition: 'all 0.3s ease'
      }}>
        <div className="emotion-icon" style={{
          fontSize: '24px',
          marginRight: '8px'
        }}>
          {getEmotionDisplay(currentEmotion).icon}
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}>
          <span style={{
            color: getEmotionDisplay(currentEmotion).color,
            fontSize: '12px',
            fontWeight: '500',
            marginBottom: '2px'
          }}>
            Current Emotion
          </span>
          <span style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '11px',
            textTransform: 'capitalize'
          }}>
            {getEmotionDisplay(currentEmotion).label}
          </span>
        </div>
      </div>


      {messages.map((message, index) => {
        const { priority, modifiedHtml } = formatMessage(message);
        return (
          <div key={index} className="observer-message-wrapper">
            <div 
              className="observer-alert"
              data-priority={priority}
              style={{ position: 'relative' }}
            >
              <div style={{
                position: 'absolute',
                bottom: '4px',
                right: '4px',
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'rgba(255,255,255,0.7)',
                borderRadius: '4px',
                padding: '1px 5px',
                fontSize: '10px',
                fontWeight: 'bold',
                lineHeight: '1.2',
                minWidth: '16px',
                textAlign: 'center'
              }}>
                {index + 1}
              </div>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modifiedHtml) }} />
            </div>
            
            <div className="feedback-controls" style={{ display: 'none' }}>
              <p>Was this advice helpful?</p>
              <div className="feedback-buttons">
                <button 
                  onClick={() => handleFeedback(index, true)}
                  className={feedbacks[index]?.helpful === true ? 'active' : ''}
                >
                  Yes
                </button>
                <button 
                  onClick={() => handleFeedback(index, false)}
                  className={feedbacks[index]?.helpful === false ? 'active' : ''}
                >
                  No
                </button>
              </div>
              {feedbacks[index]?.helpful !== null && (
                <>
                  {feedbacks[index]?.helpful === true && (
                    <div className="rating-control">
                      <p>How helpful was it? (1-10)</p>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        defaultValue="7"
                        value={feedbacks[index]?.rating || 7}
                        onChange={(e) => handleRating(index, parseInt(e.target.value))}
                      />
                      <span>{feedbacks[index]?.rating || 7}</span>
                    </div>
                  )}
                  <div className="comment-control">
                    <textarea
                      placeholder="Additional feedback (optional)"
                      value={feedbacks[index]?.comment || ''}
                      onChange={(e) => handleComment(index, e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ConversationObserver;
